import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bookmark } from "lucide-react";
import Footer from "../components/Footer";
import TopNavigator from "../components/TopNavigator";
import BookmarkModal from "../components/BookmarkModal";
import BookOpenGroupModal from "../components/BookOpenGroupModal";
import SaveBookmarkModal from "../components/SaveBookmarkModal";
import {
  adultBookColors,
  adultBookCovers,
  buildBookmarkBookMetaById,
  buildBookmarkGroups,
  DUPLICATE_GROUP_WARNING_TEXT,
  isSupportedBook,
  kidsBookColors,
  kidsBookCovers,
} from "../utils/bookmarkGroupData";
import useCtx from "../hooks/useCts";
import { useNavigate, useParams } from "react-router-dom";
import BookPDFKids from "../components/BookPDFKids";
import BookPDFAdults from "../components/BookPDFAdults";
import LottieAnimation from "../components/LottieAnimation";
import Skyline from "../assets/london-skyline.jpg";
import BookmarkSuccessAnimation from "../assets/bookmark_success.json";
import AppStoreBadge from "../assets/appstore.png";
import GooglePlayBadge from "../assets/google.png";
import QrAppStoreKids from "../assets/QR-Appstore-Kids.png";
import QrAppStoreUnlocked from "../assets/QR-Appstore-Unlocked.png";
import QrGoogleKids from "../assets/QR-Google-Kids.png";
import QrGoogleUnlocked from "../assets/QR-Google-Unlocked.png";
import {
  ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_ERROR,
  ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
  getClientElectronicBookmarks,
  isElectronicBookmarkGroupNameTooLong,
  normalizeGroupName,
  upsertElectronicBookmark,
} from "../api/editGropus";
import {
  ACTION_BUTTON_TEXT_CLASS_NAME,
  getActionButtonStyle,
  getAdaptiveActionButtonStyle,
} from "../utils/actionButtonStyles";
import { changeBookNums } from "../utils/helpers";

// ⛔ Blokady kopiowania/drukowania (CSS)
const PRINT_BLOCK_CSS = `
  /* Zablokuj druk sekcji z treścią podręcznika */
  @media print {
    #page,
    .kids-book-wrapper {
      display: none !important;
      visibility: hidden !important;
    }
  }

  /* Utrudnij zaznaczanie/kopiowanie */
  #page,
  .kids-book-wrapper {
    -webkit-user-select: none !important;
    -moz-user-select: none !important;
    -ms-user-select: none !important;
    user-select: none !important;
  }
`;

const BOOKMARK_SUCCESS_OVERLAY_CSS = `
  @keyframes bookmark-success-enter {
    from {
      opacity: 0;
      transform: scale(0.1);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

type BookOpenGroupModalState = {
  bookId: string;
  bookTitle: string;
  bookSubtitle?: string;
  bookCoverSrc?: string;
  primaryColor?: string;
  isAdultBook: boolean;
  bookmarks: ElectronicBookmark[];
};

const downloadQrSections = [
  {
    eyebrow: "Aplikacja mobilna do English Unlocked",
    title: "Skanuj i pobierz",
    cardClassName:
      "rounded-[28px] border-white/50 bg-white/10 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-sm",
    downloads: [
      {
        badgeSrc: AppStoreBadge,
        badgeAlt: "Download on the App Store",
        qrSrc: QrAppStoreUnlocked,
        qrAlt: "QR code for English Unlocked on the App Store",
      },
      {
        badgeSrc: GooglePlayBadge,
        badgeAlt: "Get it on Google Play",
        qrSrc: QrGoogleUnlocked,
        qrAlt: "QR code for English Unlocked on Google Play",
      },
    ],
  },
  {
    eyebrow: "Aplikacja mobilna do English Unlocked Kids",
    title: "Skanuj i pobierz",
    cardClassName:
      "rounded-[28px] border-[rgba(14,165,233,0.14)] bg-[linear-gradient(135deg,rgba(171,165,205,0.32)_0%,rgba(190,222,239,0.34)_52%,rgba(222,205,189,0.34)_100%)]",
    downloads: [
      {
        badgeSrc: AppStoreBadge,
        badgeAlt: "Download on the App Store",
        qrSrc: QrAppStoreKids,
        qrAlt: "QR code for English Unlocked Kids on the App Store",
      },
      {
        badgeSrc: GooglePlayBadge,
        badgeAlt: "Get it on Google Play",
        qrSrc: QrGoogleKids,
        qrAlt: "QR code for English Unlocked Kids on Google Play",
      },
    ],
  },
];

const BooksPDFScreen: React.FC = () => {
  const ctx = useCtx();
  const navigate = useNavigate();
  // 🔗 Droga B: bookId z URL (param `:bookId`), służy do dwukierunkowej synchronizacji ze state
  const { bookId: urlBookId } = useParams<{ bookId?: string }>();

  const [selectedBook, setSelectedBook] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<number | null>(null);
  const [purchasedBookIds, setPurchasedBookIds] = useState<string[]>([]);
  const [isBookmarkModalOpen, setIsBookmarkModalOpen] = useState(false);
  const [isSaveBookmarkModalOpen, setIsSaveBookmarkModalOpen] = useState(false);
  const [bookmarkModalPreferredType, setBookmarkModalPreferredType] = useState<
    "adults" | "kids" | null
  >(null);
  const [bookOpenGroupModalState, setBookOpenGroupModalState] =
    useState<BookOpenGroupModalState | null>(null);

  const [bookmarks, setBookmarks] = useState<ElectronicBookmark[]>([]);
  const [selectedGroupName, setSelectedGroupName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [isLoadingBookmarks, setIsLoadingBookmarks] = useState(false);
  const [isSavingBookmark, setIsSavingBookmark] = useState(false);
  const [isCreatingBookGroup, setIsCreatingBookGroup] = useState(false);
  const [isSaveBookmarkConfirmed, setIsSaveBookmarkConfirmed] = useState(false);
  const [openBookScrollKey, setOpenBookScrollKey] = useState(0);
  const [isBookmarkSuccessVisible, setIsBookmarkSuccessVisible] =
    useState(false);
  const [bookmarkSuccessAnimationKey, setBookmarkSuccessAnimationKey] =
    useState(0);
  const [bookmarkMessage, setBookmarkMessage] = useState<{
    type: "success" | "error" | "info" | "warning";
    text: string;
  } | null>(null);

  const books = useMemo<(BookNew | KidsBook)[]>(
    () => [...(ctx.books ?? []), ...(ctx.booksKids ?? [])],
    [ctx.books, ctx.booksKids],
  );

  // 🔗 Droga B — sync 1: URL → state.
  // Reaguje na zmianę URL (np. wpisanie /podreczniki/abc123 ręcznie, kliknięcie linka, Back/Forward).
  // Gdy URL ma bookId — ustawiamy selectedBook. Gdy URL go nie ma — czyścimy.
  useEffect(() => {
    if (urlBookId !== (selectedBook ?? undefined)) {
      setSelectedBook(urlBookId ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlBookId]);

  // 🔗 Droga B — sync 2: state → URL.
  // Reaguje na zmianę selectedBook (np. po kliknięciu książki w kafelku).
  // Push'uje URL tak, by RouteAnalytics zobaczyło zmianę pathname i zalogowało screen_view z bookId w path.
  useEffect(() => {
    if (selectedBook && urlBookId !== selectedBook) {
      navigate(`/podreczniki/${selectedBook}`);
    } else if (!selectedBook && urlBookId) {
      navigate(`/podreczniki`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBook]);

  const userClient = useMemo(() => {
    return ctx.currentClient;
  }, [ctx.currentClient]);

  const localStorageGroupKey = useMemo(
    () =>
      userClient?.id
        ? `electronic-bookmark-active-group-${userClient.id}`
        : null,
    [userClient?.id],
  );

  const activeGroupName = useMemo(() => {
    const trimmedNew = newGroupName.trim().replace(/\s+/g, " ");
    if (trimmedNew) return trimmedNew;
    return selectedGroupName.trim().replace(/\s+/g, " ");
  }, [newGroupName, selectedGroupName]);

  const sortedBooks = useMemo(
    () => [
      ...books
        .filter((book): book is BookNew => "bookNum" in book)
        .sort((a, b) => a.bookNum - b.bookNum),
      ...books
        .filter((book): book is KidsBook => "num" in book)
        .sort((a, b) => a.num - b.num),
    ],
    [books],
  );
  const visibleBooks = useMemo(
    () => sortedBooks.filter(isSupportedBook),
    [sortedBooks],
  );
  const adultVisibleBooks = useMemo(
    () => visibleBooks.filter((book): book is BookNew => "bookNum" in book),
    [visibleBooks],
  );
  const kidsVisibleBooks = useMemo(
    () => visibleBooks.filter((book): book is KidsBook => "num" in book),
    [visibleBooks],
  );

  const bookMetaById = useMemo(() => {
    return buildBookmarkBookMetaById(visibleBooks);
  }, [visibleBooks]);
  useEffect(() => {
    if (!userClient) return;

    const purchasedAdults = (ctx.books ?? []).filter((book) =>
      userClient.orderedBooks?.includes(book.id ?? ""),
    );

    const purchasedKids = (ctx.booksKids ?? []).filter((book) =>
      userClient.orderedBooks?.includes(book.id ?? ""),
    );

    const allPurchasedIds = [
      ...purchasedAdults.map((book) => book.id),
      ...purchasedKids.map((book) => book.id),
    ].filter(Boolean) as string[];

    setPurchasedBookIds(allPurchasedIds);
  }, [userClient, ctx.books, ctx.booksKids]);

  useEffect(() => {
    const loadBookmarks = async () => {
      if (!userClient?.id) {
        setBookmarks([]);
        return;
      }

      setIsLoadingBookmarks(true);

      try {
        const loadedBookmarks = await getClientElectronicBookmarks(
          userClient.id,
        );
        setBookmarks(loadedBookmarks);
      } catch (error) {
        console.error("Błąd pobierania zakładek elektronicznych:", error);
        setBookmarkMessage({
          type: "error",
          text: "Nie udało się pobrać zapisanych zakładek.",
        });
      } finally {
        setIsLoadingBookmarks(false);
      }
    };

    loadBookmarks();
  }, [userClient?.id]);

  useEffect(() => {
    if (!localStorageGroupKey) return;

    const savedGroupName = localStorage.getItem(localStorageGroupKey);
    if (savedGroupName) {
      setSelectedGroupName(savedGroupName);
      setNewGroupName("");
    }
  }, [localStorageGroupKey]);

  useEffect(() => {
    if (!localStorageGroupKey) return;
    if (!activeGroupName) return;

    localStorage.setItem(localStorageGroupKey, activeGroupName);
  }, [activeGroupName, localStorageGroupKey]);

  useEffect(() => {
    if (bookmarkMessage?.type !== "success") return;

    const clearMessageTimeout = window.setTimeout(() => {
      setBookmarkMessage((currentMessage) =>
        currentMessage?.type === "success" ? null : currentMessage,
      );
    }, 3000);

    return () => {
      window.clearTimeout(clearMessageTimeout);
    };
  }, [bookmarkMessage]);

  const selectedBookData = selectedBook
    ? books.find((book) => book.id === selectedBook)
    : null;

  const selectedBookMeta = selectedBookData?.id
    ? (bookMetaById.get(selectedBookData.id) ?? null)
    : null;

  const getBookmarksForBook = (bookId: string) => {
    if (!userClient?.id) return null;

    return bookmarks.filter(
      (bookmark) =>
        bookmark.clientId === userClient.id && bookmark.bookId === bookId,
    );
  };

  const getUniqueBookmarksForBook = (bookId: string) => {
    const bookmarksForBook = getBookmarksForBook(bookId) ?? [];
    const uniqueBookmarks = new Map<string, ElectronicBookmark>();

    [...bookmarksForBook]
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .forEach((bookmark) => {
        const groupKey =
          bookmark.groupNameNormalized ??
          normalizeGroupName(bookmark.groupName);

        if (!uniqueBookmarks.has(groupKey)) {
          uniqueBookmarks.set(groupKey, bookmark);
        }
      });

    return Array.from(uniqueBookmarks.values());
  };

  const getAutoBookmarkForBook = (bookId: string) => {
    const uniqueBookmarksForBook = getUniqueBookmarksForBook(bookId);

    if (uniqueBookmarksForBook.length === 0) {
      return { bookmark: null, groupCount: 0 };
    }

    if (uniqueBookmarksForBook.length !== 1) {
      return { bookmark: null, groupCount: uniqueBookmarksForBook.length };
    }

    return {
      bookmark: uniqueBookmarksForBook[0] ?? null,
      groupCount: 1,
    };
  };

  const bookmarkGroups = useMemo(() => {
    return buildBookmarkGroups({
      visibleBooks,
      purchasedBookIds,
      bookmarks,
      bookMetaById,
    });
  }, [bookmarks, bookMetaById, purchasedBookIds, visibleBooks]);

  const bookIdsWithAssignedGroups = useMemo(
    () => new Set(bookmarks.map((bookmark) => bookmark.bookId)),
    [bookmarks],
  );

  const formatBookmarkPage = (book: BookNew | KidsBook, pageNum: number) =>
    "pages" in book ? changeBookNums(pageNum) : pageNum;

  const scrollToOpenedBook = useRef(false);
  const openedBookContentRef = useRef<HTMLDivElement | null>(null);
  const adultBooksSectionRef = useRef<HTMLDivElement | null>(null);
  const kidsBooksSectionRef = useRef<HTMLDivElement | null>(null);
  const saveBookmarkCloseTimeoutRef = useRef<number | null>(null);
  const bookmarkSuccessStartTimeoutRef = useRef<number | null>(null);
  const bookmarkSuccessTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (saveBookmarkCloseTimeoutRef.current) {
        window.clearTimeout(saveBookmarkCloseTimeoutRef.current);
      }

      if (bookmarkSuccessStartTimeoutRef.current) {
        window.clearTimeout(bookmarkSuccessStartTimeoutRef.current);
      }

      if (bookmarkSuccessTimeoutRef.current) {
        window.clearTimeout(bookmarkSuccessTimeoutRef.current);
      }
    };
  }, []);

  const showBookmarkSuccess = (delayMs = 0) => {
    if (bookmarkSuccessStartTimeoutRef.current) {
      window.clearTimeout(bookmarkSuccessStartTimeoutRef.current);
      bookmarkSuccessStartTimeoutRef.current = null;
    }

    if (bookmarkSuccessTimeoutRef.current) {
      window.clearTimeout(bookmarkSuccessTimeoutRef.current);
      bookmarkSuccessTimeoutRef.current = null;
    }

    const revealSuccess = () => {
      setBookmarkSuccessAnimationKey((prev) => prev + 1);
      setIsBookmarkSuccessVisible(true);

      bookmarkSuccessTimeoutRef.current = window.setTimeout(() => {
        setIsBookmarkSuccessVisible(false);
        bookmarkSuccessTimeoutRef.current = null;
      }, 1600);
    };

    if (delayMs > 0) {
      setIsBookmarkSuccessVisible(false);
      bookmarkSuccessStartTimeoutRef.current = window.setTimeout(() => {
        bookmarkSuccessStartTimeoutRef.current = null;
        revealSuccess();
      }, delayMs);
      return;
    }

    revealSuccess();
  };

  const scrollOpenedBookIntoView = () => {
    const target =
      document.getElementById("adult-book-navigation") ??
      document.getElementById("page") ??
      document.getElementById("kids-book-header") ??
      document.getElementById("kids-book-page") ??
      openedBookContentRef.current;

    target?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    if (!selectedBookData) {
      setSelectedPage(null);
    }
  }, [selectedBookData]);

  useEffect(() => {
    if (
      scrollToOpenedBook.current &&
      selectedBookData &&
      (!("pages" in selectedBookData) || selectedPage !== null)
    ) {
      const finishScroll = () => {
        scrollOpenedBookIntoView();
        scrollToOpenedBook.current = false;
      };

      if ("pages" in selectedBookData) {
        finishScroll();
      } else {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(finishScroll);
        });
      }
    }
  }, [selectedBookData, selectedPage, openBookScrollKey]);

  const handleOpenBook = (bookId: string, isAdultBook: boolean) => {
    setBookmarkMessage(null);
    setIsSaveBookmarkModalOpen(false);
    setIsBookmarkModalOpen(false);

    const bookData = books.find((book) => book.id === bookId);
    const { groupCount } = getAutoBookmarkForBook(bookId);
    const bookBookmarks = getUniqueBookmarksForBook(bookId);

    if (bookData?.id && groupCount >= 1) {
      const bookMeta = bookMetaById.get(bookData.id);

      scrollToOpenedBook.current = false;
      setBookOpenGroupModalState({
        bookId: bookData.id,
        bookTitle:
          bookMeta?.bookTitle ??
          (isAdultBook
            ? `English Unlocked ${(bookData as BookNew).bookNum}`
            : `English Unlocked ${(bookData as KidsBook).num}`),
        bookSubtitle: bookMeta?.bookSubtitle,
        bookCoverSrc: isAdultBook
          ? adultBookCovers[(bookData as BookNew).bookNum]
          : kidsBookCovers[(bookData as KidsBook).num],
        primaryColor: isAdultBook
          ? (adultBookColors[(bookData as BookNew).bookNum] ?? "#f6851f")
          : (kidsBookColors[(bookData as KidsBook).num] ?? "#f6851f"),
        isAdultBook,
        bookmarks: bookBookmarks,
      });
      return;
    }

    scrollToOpenedBook.current = true;
    setOpenBookScrollKey((prev) => prev + 1);
    setBookOpenGroupModalState(null);

    setSelectedBook(bookId);
    setSelectedGroupName("");
    setNewGroupName("");

    if (isAdultBook) {
      setSelectedPage(0);

      return;
    }

    setSelectedPage(1);
  };

  const handleCloseBookOpenGroupModal = () => {
    setBookmarkMessage(null);
    setBookOpenGroupModalState(null);
  };

  const handleCreateBookGroup = async (bookId: string, groupName: string) => {
    if (!userClient?.id || !bookId) {
      throw new Error("missing-book-context");
    }

    if (isElectronicBookmarkGroupNameTooLong(groupName)) {
      setBookmarkMessage({
        type: "error",
        text: ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
      });
      throw new Error(ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_ERROR);
    }

    const normalizedGroupName = normalizeGroupName(groupName);
    const existingGroup = bookmarks.find(
      (bookmark) =>
        bookmark.clientId === userClient.id &&
        bookmark.bookId === bookId &&
        bookmark.groupNameNormalized === normalizedGroupName,
    );

    if (existingGroup) {
      setBookmarkMessage({
        type: "warning",
        text: DUPLICATE_GROUP_WARNING_TEXT,
      });
      throw new Error("group-already-exists");
    }

    setIsCreatingBookGroup(true);

    try {
      setBookmarkMessage(null);
      const createdGroup = await upsertElectronicBookmark({
        clientId: userClient.id,
        groupName,
        bookId,
        pageNum: null,
        updatedBy: ctx.user?.email ?? undefined,
      });

      setBookmarks((prev) => {
        const filtered = prev.filter(
          (bookmark) => bookmark.id !== createdGroup.id,
        );
        return [...filtered, createdGroup];
      });
      setSelectedGroupName(createdGroup.groupName);
      setNewGroupName("");
    } catch (error) {
      if (
        (error as Error).message ===
        ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_ERROR
      ) {
        throw error;
      }

      console.error("Błąd tworzenia grupy zakładki:", error);
      setBookmarkMessage({
        type: "error",
        text: "Nie udało się utworzyć grupy.",
      });
      throw error;
    } finally {
      setIsCreatingBookGroup(false);
    }
  };

  const handleTypeNewGroup = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewGroupName(e.target.value);
    setBookmarkMessage(null);
  };

  const handleOpenSavedBookmark = (bookmark: ElectronicBookmark) => {
    const bookData = books.find((book) => book.id === bookmark.bookId);

    if (!bookData?.id) {
      setBookmarkMessage({
        type: "error",
        text: "Nie udało się otworzyć wybranej zakładki.",
      });
      return;
    }

    scrollToOpenedBook.current = true;
    setOpenBookScrollKey((prev) => prev + 1);
    setSelectedBook(bookmark.bookId);
    setSelectedGroupName(bookmark.groupName);
    setNewGroupName("");
    setBookmarkMessage(null);
    setIsSaveBookmarkModalOpen(false);
    setIsBookmarkModalOpen(false);

    const savedPageNum =
      typeof bookmark.pageNum === "number" ? bookmark.pageNum : null;
    const hasSavedBookmark = savedPageNum !== null;

    if ("pages" in bookData) {
      const totalPages = bookData.pages.length;
      const safePage =
        hasSavedBookmark && totalPages > 0
          ? Math.min(Math.max(savedPageNum, 1), totalPages)
          : 1;

      setSelectedPage(totalPages > 0 ? safePage - 1 : 0);
      return;
    }

    const safePage = hasSavedBookmark ? Math.max(savedPageNum, 1) : 1;
    setSelectedPage(safePage);
  };

  const handleOpenBookGroupBookmark = (bookmark: ElectronicBookmark) => {
    setBookOpenGroupModalState(null);
    handleOpenSavedBookmark(bookmark);
  };

  const saveBookmarkToGroup = async (
    groupName: string,
    options?: {
      showConfirmedState?: boolean;
      closeSaveModalAfterSuccess?: boolean;
      closeSaveModalOnSubmit?: boolean;
      successDelayMs?: number;
    },
  ) => {
    if (!userClient?.id) {
      setBookmarkMessage({
        type: "error",
        text: "Nie udało się ustalić szkoły dla tej zakładki.",
      });
      return false;
    }

    if (!selectedBookData?.id) {
      setBookmarkMessage({
        type: "error",
        text: "Najpierw otwórz podręcznik.",
      });
      return false;
    }

    const cleanedGroupName = groupName.trim().replace(/\s+/g, " ");
    const normalizedGroupName = normalizeGroupName(cleanedGroupName);
    const existingGroup = bookmarks.find(
      (bookmark) =>
        bookmark.clientId === userClient.id &&
        bookmark.bookId === selectedBookData.id &&
        bookmark.groupNameNormalized === normalizedGroupName,
    );

    if (!existingGroup && isElectronicBookmarkGroupNameTooLong(groupName)) {
      setBookmarkMessage({
        type: "error",
        text: ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
      });
      return false;
    }

    if (!cleanedGroupName) {
      setBookmarkMessage({
        type: "error",
        text: "Wpisz nazwę nowej grupy.",
      });
      return false;
    }

    if (selectedPage === null) {
      setBookmarkMessage({
        type: "error",
        text: "Nie udało się odczytać aktualnej strony.",
      });
      return false;
    }

    const pageNumToSave =
      "pages" in selectedBookData ? selectedPage + 1 : selectedPage;

    if (options?.closeSaveModalOnSubmit) {
      if (saveBookmarkCloseTimeoutRef.current) {
        window.clearTimeout(saveBookmarkCloseTimeoutRef.current);
        saveBookmarkCloseTimeoutRef.current = null;
      }

      setIsSaveBookmarkConfirmed(false);
      setIsSaveBookmarkModalOpen(false);
    }

    setIsSavingBookmark(true);
    if (options?.showConfirmedState) {
      setIsSaveBookmarkConfirmed(false);
    }

    try {
      const savedBookmark = await upsertElectronicBookmark({
        clientId: userClient.id,
        groupName: cleanedGroupName,
        bookId: selectedBookData.id,
        pageNum: pageNumToSave,
        updatedBy: ctx.user?.email ?? undefined,
      });

      setBookmarks((prev) => {
        const filtered = prev.filter(
          (bookmark) => bookmark.id !== savedBookmark.id,
        );
        return [...filtered, savedBookmark];
      });

      setSelectedGroupName(savedBookmark.groupName);
      setNewGroupName("");

      setBookmarkMessage({
        type: "success",
        text: `Zapisano zakładkę dla grupy „${cleanedGroupName}” na stronie ${formatBookmarkPage(
          selectedBookData,
          pageNumToSave,
        )}.`,
      });
      if (options?.showConfirmedState) {
        setIsSaveBookmarkConfirmed(true);
      }
      showBookmarkSuccess(options?.successDelayMs);

      if (options?.closeSaveModalAfterSuccess) {
        if (saveBookmarkCloseTimeoutRef.current) {
          window.clearTimeout(saveBookmarkCloseTimeoutRef.current);
        }

        saveBookmarkCloseTimeoutRef.current = window.setTimeout(() => {
          setIsSaveBookmarkModalOpen(false);
          setIsSaveBookmarkConfirmed(false);
          saveBookmarkCloseTimeoutRef.current = null;
        }, 700);
      }

      return true;
    } catch (error) {
      console.error("Błąd zapisywania zakładki elektronicznej:", error);
      setBookmarkMessage(
        (error as Error).message ===
          ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_ERROR
          ? {
              type: "error",
              text: ELECTRONIC_BOOKMARK_GROUP_NAME_TOO_LONG_TEXT,
            }
          : {
              type: "error",
              text: "Nie udało się zapisać zakładki.",
            },
      );

      if (options?.closeSaveModalOnSubmit) {
        if (bookmarkSuccessStartTimeoutRef.current) {
          window.clearTimeout(bookmarkSuccessStartTimeoutRef.current);
          bookmarkSuccessStartTimeoutRef.current = null;
        }
        if (bookmarkSuccessTimeoutRef.current) {
          window.clearTimeout(bookmarkSuccessTimeoutRef.current);
          bookmarkSuccessTimeoutRef.current = null;
        }
        setIsBookmarkSuccessVisible(false);
        setIsSaveBookmarkModalOpen(true);
      }

      return false;
    } finally {
      setIsSavingBookmark(false);
    }
  };

  const handleSaveBookmark = async () => {
    await saveBookmarkToGroup(newGroupName, {
      closeSaveModalOnSubmit: true,
      successDelayMs: 140,
    });
  };

  const handleCloseSaveBookmarkModal = () => {
    if (saveBookmarkCloseTimeoutRef.current) {
      window.clearTimeout(saveBookmarkCloseTimeoutRef.current);
      saveBookmarkCloseTimeoutRef.current = null;
    }

    setIsSaveBookmarkConfirmed(false);
    setIsSaveBookmarkModalOpen(false);
    setBookmarkMessage(null);
  };

  const handleOpenSaveBookmarkModal = () => {
    setBookmarkMessage(null);
    setIsSaveBookmarkConfirmed(false);
    setIsSaveBookmarkModalOpen(true);
  };

  const handleOpenBookBookmarkAction = () => {
    if (!selectedBookData?.id || !userClient?.id) {
      setBookmarkMessage({
        type: "error",
        text: "Najpierw otwórz podręcznik.",
      });
      return;
    }

    const normalizedSelectedGroup = normalizeGroupName(selectedGroupName);
    const selectedGroupForCurrentBook =
      normalizedSelectedGroup.length > 0
        ? (bookmarks.find(
            (bookmark) =>
              bookmark.clientId === userClient.id &&
              bookmark.bookId === selectedBookData.id &&
              bookmark.groupNameNormalized === normalizedSelectedGroup,
          ) ?? null)
        : null;
    const existingGroupsForCurrentBook = getUniqueBookmarksForBook(
      selectedBookData.id,
    );
    const fallbackGroupForCurrentBook =
      existingGroupsForCurrentBook.find(
        (bookmark) =>
          bookmark.clientId === userClient.id &&
          bookmark.bookId === selectedBookData.id,
      ) ?? null;

    setBookmarkMessage(null);

    if (selectedGroupForCurrentBook) {
      void saveBookmarkToGroup(selectedGroupForCurrentBook.groupName);
      return;
    }

    if (fallbackGroupForCurrentBook) {
      setSelectedGroupName(fallbackGroupForCurrentBook.groupName);
      setNewGroupName("");
      void saveBookmarkToGroup(fallbackGroupForCurrentBook.groupName);
      return;
    }

    setIsBookmarkModalOpen(false);
    handleOpenSaveBookmarkModal();
  };

  const handleOpenBookmarkModal = (
    preferredType: "adults" | "kids" | null = null,
  ) => {
    setBookmarkModalPreferredType(preferredType);
    setIsBookmarkModalOpen(true);
  };

  const handleCloseBookmarkModal = () => {
    setBookmarkMessage(null);
    setIsBookmarkModalOpen(false);
    setBookmarkModalPreferredType(null);
  };

  const handleScrollToMobileBookSection = (bookType: "adults" | "kids") => {
    const target =
      bookType === "adults"
        ? adultBooksSectionRef.current
        : kidsBooksSectionRef.current;

    target?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const getBookCardActionButtonStyle = (bookData?: BookNew | KidsBook) => {
    if (!bookData) {
      return getActionButtonStyle("orange");
    }

    if (!("pages" in bookData)) {
      return getActionButtonStyle("sky");
    }

    if (bookData.bookNum === 2) {
      return getActionButtonStyle("orange");
    }

    return getAdaptiveActionButtonStyle(
      adultBookColors[bookData.bookNum] ?? "#F6851F",
    );
  };

  const BuyButton: React.FC<{ bookId: string }> = ({ bookId }) => {
    const bookData = books.find((book) => book.id === bookId);
    const isAdultBook = bookData && "pages" in bookData;

    return (
      <button
        onClick={() =>
          navigate("/zamowienia", {
            state: {
              scrollToSectionId: isAdultBook
                ? "order-adults-students"
                : "order-kids-students",
            },
          })
        }
        className={`hidden absolute top-[75%] left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full px-4 py-2.5 sm:flex sm:px-6 sm:opacity-0 sm:group-hover:opacity-100 sm:transition-opacity sm:duration-300 ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
        style={getBookCardActionButtonStyle(bookData)}
      >
        Zamów
      </button>
    );
  };

  const OpenButton: React.FC<{ bookId: string }> = ({ bookId }) => {
    const bookData = books.find((book) => book.id === bookId);

    return (
      <button
        type="button"
        className={`hidden absolute top-[75%] left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full px-4 py-2.5 sm:flex sm:px-6 sm:opacity-0 sm:group-hover:opacity-100 sm:transition-opacity sm:duration-300 ${ACTION_BUTTON_TEXT_CLASS_NAME}`}
        style={getBookCardActionButtonStyle(bookData)}
      >
        Otwórz
      </button>
    );
  };

  const renderBookCard = (book: BookNew | KidsBook) => {
    const isAdultBook = "bookNum" in book;
    const bookNumber = isAdultBook ? book.bookNum : book.num;
    const bookImage = isAdultBook
      ? adultBookCovers[bookNumber]
      : kidsBookCovers[bookNumber];
    const isPurchased = purchasedBookIds.includes(book.id ?? "");
    const hasAssignedGroup = book.id
      ? bookIdsWithAssignedGroups.has(book.id)
      : false;
    const bookKey =
      book.id ?? `${isAdultBook ? "adult" : "kids"}-${bookNumber}`;

    return (
      <div key={bookKey} className="relative rounded-[24px] bg-white/40 p-3">
        {hasAssignedGroup && (
          <div className="pointer-events-none absolute right-6 -top-1 z-10">
            <Bookmark
              size={34}
              strokeWidth={2}
              fill="#ffbc08"
              color="#ffbc08"
              aria-hidden="true"
            />
          </div>
        )}
        <div
          className="group relative cursor-pointer overflow-hidden rounded-[18px] sm:transition sm:transform sm:hover:scale-[1.02]"
          onClick={() =>
            isPurchased && book.id && handleOpenBook(book.id, isAdultBook)
          }
        >
          <img
            src={bookImage}
            alt={isAdultBook ? `Book ${bookNumber}` : `Kids Book ${bookNumber}`}
            className={`h-auto w-full ${isPurchased ? "" : "filter grayscale"}`}
          />
          {!ctx.isTeacher && !isPurchased && book.id && (
            <BuyButton bookId={book.id} />
          )}
          {isPurchased && book.id && <OpenButton bookId={book.id} />}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="relative flex min-h-screen flex-col bg-[#f7f2ea] text-gray-700">
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-cover bg-center bg-no-repeat opacity-40"
          style={{ backgroundImage: `url(${Skyline})` }}
        />
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-[linear-gradient(180deg,rgba(255,250,245,0.82),rgba(255,255,255,0.66)_24%,rgba(248,250,252,0.5))]"
        />
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-[radial-gradient(circle_at_top_left,rgba(246,133,31,0.18),transparent_50%),radial-gradient(circle_at_88%_18%,rgba(44,150,233,0.24),transparent_54%),linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.24))]"
        />

        <div className="relative z-20 flex flex-1 flex-col">
          <TopNavigator />
          <style>{PRINT_BLOCK_CSS}</style>

          <main className="mx-auto w-full max-w-7xl px-4 pb-10 pt-6 sm:px-6 lg:px-8">
            <section className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_360px] lg:items-start">
              <div className="px-1 pt-4 sm:pt-8 lg:pt-12">
                <h1 className="max-w-3xl bg-[linear-gradient(135deg,#0f172a_10%,#9a5b1a_48%,#334155_92%)] bg-clip-text pb-1 text-3xl font-semibold leading-[1.12] tracking-[-0.04em] text-transparent sm:text-4xl lg:text-5xl">
                  Twoje podręczniki w wersji cyfrowej.
                </h1>

                <div className="mt-5 flex gap-3 sm:hidden">
                  <button
                    type="button"
                    onClick={() => handleScrollToMobileBookSection("adults")}
                    className="flex-1 rounded-full border border-transparent bg-[linear-gradient(135deg,#f59e0b,#f97316)] px-4 py-3 text-sm font-semibold tracking-[0.04em] text-white shadow-[0_10px_24px_rgba(249,115,22,0.2)] transition"
                  >
                    Adults
                  </button>
                  <button
                    type="button"
                    onClick={() => handleScrollToMobileBookSection("kids")}
                    className="flex-1 rounded-full border border-transparent bg-[linear-gradient(135deg,#38bdf8,#0ea5e9)] px-4 py-3 text-sm font-semibold tracking-[0.04em] text-white shadow-[0_10px_24px_rgba(14,165,233,0.2)] transition"
                  >
                    Kids
                  </button>
                </div>
              </div>

              <aside className="hidden w-full sm:block lg:max-w-[360px] lg:justify-self-end">
                <div className="rounded-[28px] border border-white/50 bg-white/10 p-5 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-sm sm:p-6">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] border border-white/30 shadow-[0_14px_30px_rgba(148,163,184,0.36)]">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-black/5 bg-white/50 text-slate-700">
                        <Bookmark
                          size={18}
                          strokeWidth={2.2}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">
                        Elektroniczne
                      </p>
                      <p className="mt-1 text-2xl font-semibold tracking-[-0.03em] text-slate-900">
                        Zakładki
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <div className="flex flex-col items-center rounded-2xl border border-white/50 bg-white/10 p-4 text-center">
                      <p className="text-sm text-slate-500">Adults</p>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => handleOpenBookmarkModal("adults")}
                          className="inline-flex h-16 w-16 items-center justify-center rounded-[22px] bg-white/35 transition hover:bg-white/55"
                        >
                          <Bookmark
                            size={34}
                            strokeWidth={1.8}
                            fill="#ffbc08"
                            color="#ffbc08"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col items-center rounded-2xl border border-white/50 bg-white/10 p-4 text-center">
                      <p className="text-sm text-slate-500">Kids</p>
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => handleOpenBookmarkModal("kids")}
                          className="inline-flex h-16 w-16 items-center justify-center rounded-[22px] bg-white/35 transition hover:bg-white/55"
                        >
                          <Bookmark
                            size={34}
                            strokeWidth={1.8}
                            fill="#0ea5e9"
                            color="#0ea5e9"
                            aria-hidden="true"
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </aside>
            </section>

            <section className="mt-4 hidden lg:block">
              <div className="grid grid-cols-2 gap-3">
                {downloadQrSections.map((section) => (
                  <div
                    key={section.eyebrow}
                    className={`border p-4 ${section.cardClassName}`}
                  >
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                        {section.eyebrow}
                      </p>
                      <p className="mt-0.5 text-base font-semibold tracking-[-0.02em] text-slate-900">
                        {section.title}
                      </p>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3">
                      {section.downloads.map((download) => (
                        <div key={download.qrAlt} className="text-center">
                          <img
                            src={download.badgeSrc}
                            alt={download.badgeAlt}
                            className="mx-auto w-full max-w-[132px] object-contain"
                          />
                          <img
                            src={download.qrSrc}
                            alt={download.qrAlt}
                            className="mx-auto mt-3 aspect-square w-full max-w-[132px]"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="mt-10">
              <div className="rounded-[28px] border border-white/50 p-4 shadow-[0_20px_60px_rgba(148,163,184,0.18)] backdrop-blur-sm sm:p-6">
                <div className="space-y-8 sm:hidden">
                  <div ref={adultBooksSectionRef} className="scroll-mt-6">
                    <div className="mb-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                        Adults
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {adultVisibleBooks.map((book) => renderBookCard(book))}
                    </div>
                  </div>

                  <div ref={kidsBooksSectionRef} className="scroll-mt-6">
                    <div className="mb-4">
                      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">
                        Kids
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {kidsVisibleBooks.map((book) => renderBookCard(book))}
                    </div>
                  </div>
                </div>

                <div className="hidden grid-cols-2 gap-4 sm:grid sm:grid-cols-3 sm:gap-6 lg:grid-cols-6">
                  {visibleBooks.map((book) => renderBookCard(book))}
                </div>
              </div>
            </section>
          </main>

          {selectedBookData &&
            "pages" in selectedBookData &&
            selectedPage !== null &&
            selectedPage < selectedBookData.pages.length && (
              <div ref={openedBookContentRef} className="w-full">
                <BookPDFAdults
                  book={selectedBookData as BookNew}
                  selectedPage={selectedPage}
                  onChangePage={(n) => setSelectedPage(n)}
                  onOpenSaveBookmarkModal={handleOpenBookBookmarkAction}
                  primaryColor={
                    adultBookColors[(selectedBookData as BookNew).bookNum] ??
                    "#F6851F"
                  }
                />
              </div>
            )}

          {selectedBookData && !("pages" in selectedBookData) && (
            <div
              ref={openedBookContentRef}
              className="mt-6 max-w-4xl w-full mx-auto pb-8"
              style={{ minHeight: "400px" }}
            >
              <BookPDFKids
                book={selectedBookData as KidsBook}
                selectedPage={selectedPage ?? 1}
                onChangePage={(n) => setSelectedPage(n)}
                onOpenSaveBookmarkModal={handleOpenBookBookmarkAction}
              />
            </div>
          )}

          <BookmarkModal
            isOpen={isBookmarkModalOpen}
            onClose={handleCloseBookmarkModal}
            preferredBookType={bookmarkModalPreferredType}
            activeGroupName={activeGroupName}
            selectedBookId={selectedBookData?.id ?? null}
            bookmarkGroups={bookmarkGroups}
            onCreateGroup={handleCreateBookGroup}
            isCreatingGroup={isCreatingBookGroup}
            onOpenSavedBookmark={handleOpenSavedBookmark}
            isLoadingBookmarks={isLoadingBookmarks}
            onClearMessage={() =>
              setBookmarkMessage((prev) =>
                prev?.type === "warning" ? null : prev,
              )
            }
            bookmarkMessage={bookmarkMessage}
          />
          <BookOpenGroupModal
            isOpen={Boolean(bookOpenGroupModalState)}
            onClose={handleCloseBookOpenGroupModal}
            bookTitle={bookOpenGroupModalState?.bookTitle ?? null}
            bookSubtitle={bookOpenGroupModalState?.bookSubtitle ?? null}
            bookCoverSrc={bookOpenGroupModalState?.bookCoverSrc ?? null}
            primaryColor={bookOpenGroupModalState?.primaryColor ?? "#f6851f"}
            isAdultBook={bookOpenGroupModalState?.isAdultBook ?? true}
            bookmarks={
              bookOpenGroupModalState?.bookId
                ? getUniqueBookmarksForBook(bookOpenGroupModalState.bookId)
                : []
            }
            onOpenBookmark={handleOpenBookGroupBookmark}
            onCreateGroup={(groupName) =>
              bookOpenGroupModalState?.bookId
                ? handleCreateBookGroup(
                    bookOpenGroupModalState.bookId,
                    groupName,
                  )
                : Promise.reject(new Error("missing-book-context"))
            }
            isCreatingGroup={isCreatingBookGroup}
            onClearMessage={() =>
              setBookmarkMessage((prev) =>
                prev?.type === "warning" ? null : prev,
              )
            }
            bookmarkMessage={bookmarkMessage}
          />
          <SaveBookmarkModal
            isOpen={isSaveBookmarkModalOpen}
            onClose={handleCloseSaveBookmarkModal}
            newGroupName={newGroupName}
            selectedBookTitle={selectedBookMeta?.bookTitle ?? null}
            onTypeNewGroup={handleTypeNewGroup}
            onSaveBookmark={handleSaveBookmark}
            isSavingBookmark={isSavingBookmark}
            isSaveConfirmed={isSaveBookmarkConfirmed}
            bookmarkMessage={bookmarkMessage}
          />
          {isBookmarkSuccessVisible && (
            <div className="pointer-events-none fixed inset-0 z-[130] flex items-center justify-center px-4 backdrop-blur-sm">
              <style>{BOOKMARK_SUCCESS_OVERLAY_CSS}</style>
              <div
                className="flex h-[36rem] w-[36rem] max-h-[calc(100vh-2rem)] max-w-[calc(100vw-2rem)] items-center justify-center"
                style={{
                  animation:
                    "bookmark-success-enter 80ms cubic-bezier(0.22, 1, 0.36, 1)",
                }}
              >
                <LottieAnimation
                  key={`bookmark-success-${bookmarkSuccessAnimationKey}`}
                  animationData={BookmarkSuccessAnimation}
                  loop={false}
                  autoplay
                  className="h-full w-full"
                />
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
};

export default BooksPDFScreen;
